import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../../data/base";
import dayjs from "dayjs";
import { getMetricsForReport, generateExcelFile, generateExcelFileWithSections, enrichDataWithSubcollections } from "../data/analyticsData";
import { getTimeInSecs, convertiSecondiAMinuti } from "../utils/analyticsUtils";

export const getAggregatedData = async (catalogIds, startDate, endDate) => {
    try {
        // Prepara i catalogIds: assicuriamoci che siano tutti stringhe per il confronto
        const normalizedCatalogIds = catalogIds.map(id => String(id));

        // Preparazione della query
        const q = query(
            collection(db, "AggregateSessions"),
            where("date", ">=", startDate),
            where("date", "<=", endDate)
        );

        // Esecuzione della query
        const snapshot = await getDocs(q);

        // Creiamo una mappa di oggetti doc per ID catalogo per avere una panoramica chiara
        const docsByCatalog = {};

        // Per ogni documento, registra il catalog
        const catalogMap = new Map();
        snapshot.docs.forEach(doc => {
            const data = doc.data();
            const catalog = data.catalog || "unknown";

            // Conteggio documenti per catalogo
            if (!catalogMap.has(catalog)) {
                catalogMap.set(catalog, 0);
                docsByCatalog[catalog] = [];
            }
            catalogMap.set(catalog, catalogMap.get(catalog) + 1);
            docsByCatalog[catalog].push(doc.id);
        });

        // Filtraggio dei risultati in base ai catalogIds
        const filteredResult = snapshot.docs.filter((doc) => {
            const sessionData = doc.data();
            const catalog = sessionData.catalog || "unknown";

            // Controllo esplicito se il catalogo è incluso nell'array normalizzato
            const isCatalogIncluded = normalizedCatalogIds.includes(String(catalog));

            return isCatalogIncluded;
        });

        // Conteggio dei cataloghi nei risultati filtrati
        const filteredCatalogMap = new Map();
        filteredResult.forEach(doc => {
            const data = doc.data();
            const catalog = data.catalog || "unknown";
            if (!filteredCatalogMap.has(catalog)) {
                filteredCatalogMap.set(catalog, 0);
            }
            filteredCatalogMap.set(catalog, filteredCatalogMap.get(catalog) + 1);
        });

        // Creazione di un array di oggetti con i dati e gli ID dei documenti
        const aggregatedData = filteredResult.map((doc) => ({
            ...doc.data(),
            id: doc.id,
        }));

        return aggregatedData;
    } catch (error) {
        return [];
    }
};

export async function generateReportWithSections(startDate, endDate, selectedCatalogues, enrichedData = null, reportSections = null) {
    const formatDate = (date) => dayjs(date).format("YYYY/MM/DD");
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    try {
        const cataloguesArray = Array.isArray(selectedCatalogues) ? selectedCatalogues.map(String) : [];

        // Se abbiamo già i dati arricchiti e filtrati, li usiamo direttamente
        let aggregateList = [];
        if (enrichedData && Array.isArray(enrichedData)) {
            aggregateList = enrichedData;
        } else {
            // Altrimenti recuperiamo i dati dalle API
            aggregateList = await getAggregatedData(
                cataloguesArray.length > 0 ? cataloguesArray : null,
                formattedStartDate,
                formattedEndDate
            );

            // Arricchisci i dati con le sottocollezioni
            try {
                aggregateList = await enrichDataWithSubcollections(aggregateList);
            } catch (error) {
                console.error("Error enriching data:", error);
            }
        }

        // Fetch dei cataloghi selezionati
        const cataloguePromises = cataloguesArray.map(async (id) => {
            try {
                const catalogueDoc = await getDoc(doc(db, "Catalogues", id));
                if (catalogueDoc.exists()) {
                    return { id, ...catalogueDoc.data() };
                }
                return { id, nameCatalog: id };
            } catch (error) {
                console.error(`Error fetching catalogue ${id}:`, error);
                return { id, nameCatalog: id };
            }
        });

        let validResults = (await Promise.all(cataloguePromises)).filter(Boolean);

        // Se non ci sono cataloghi validi, gestisci tutti i cataloghi
        if (validResults.length === 0 || cataloguesArray.length === 0) {
            validResults = [{ id: 'all', nameCatalog: 'ALL CATALOGUES' }];
        }

        // Se abbiamo più di un catalogo e nessuno è 'all', aggiungi i cataloghi individuali
        if (cataloguesArray.length > 1 && !cataloguesArray.includes('all')) {
            // Mantieni i cataloghi individuali per la riga totals
            validResults = validResults.filter(cat => cat.id !== 'all');
        }

        // Genera il file Excel con le sezioni selezionate
        generateExcelFileWithSections(aggregateList, validResults, reportSections, formattedStartDate, formattedEndDate);

        return true;
    } catch (error) {
        console.error("Error generating report:", error);
        throw error;
    }
}

export async function generateReport(startDate, endDate, selectedCatalogues, selectedOptions, enrichedData = null) {
    const formatDate = (date) => dayjs(date).format("YYYY/MM/DD");
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    try {
        const cataloguesArray = Array.isArray(selectedCatalogues) ? selectedCatalogues.map(String) : [];

        // Se abbiamo già i dati arricchiti e filtrati, li usiamo direttamente
        let aggregateList = [];
        if (enrichedData && Array.isArray(enrichedData)) {
            aggregateList = enrichedData;
        } else {
            // Altrimenti recuperiamo i dati dalle API
            // 1️⃣ Fetch dei dati aggregati
            aggregateList = await getAggregatedData(
                cataloguesArray.length > 0 ? cataloguesArray : null,
                formattedStartDate,
                formattedEndDate
            );

            // Arricchisci i dati con le sottocollezioni
            try {
                aggregateList = await enrichDataWithSubcollections(aggregateList);
            } catch (error) {
                console.error("Error enriching data:", error);
                // Continua con i dati non arricchiti
            }
        }

        // 2️⃣ Fetch dei cataloghi selezionati
        // Per avere i nomi corretti dei cataloghi
        const cataloguePromises = cataloguesArray.map(async (id) => {
            try {
                const catalogueDoc = await getDoc(doc(db, "Catalogues", id));
                if (catalogueDoc.exists()) {
                    return { id, ...catalogueDoc.data() };
                }
                return { id, nameCatalog: id };
            } catch (error) {
                console.error(`Error fetching catalogue ${id}:`, error);
                return { id, nameCatalog: id };
            }
        });

        let validResults = (await Promise.all(cataloguePromises)).filter(Boolean);

        // Se non ci sono cataloghi validi o è selezionato 'all', gestisci tutti i cataloghi
        if (validResults.length === 0 || cataloguesArray.includes('all') || cataloguesArray.length === 0) {
            // Aggiunge un'opzione "ALL" per i totali
            validResults = [{ id: 'all', nameCatalog: 'ALL CATALOGUES' }];
        }

        // Raggruppa i dati per catalogo - come fatto nell'interfaccia
        const catalogueData = {};

        // Inizializza i dati per ogni catalogo
        validResults.forEach(catalogue => {
            catalogueData[catalogue.id] = {
                catalogue: catalogue,
                sessions: []
            };
        });

        // Debug dei tipi di servizio presenti
        const servicesSet = new Set();
        aggregateList.forEach(session => {
            if (session && session.service) {
                servicesSet.add(session.service);
            }
        });

        // Distribuisci le sessioni ai cataloghi corrispondenti
        aggregateList.forEach(session => {
            const catalogueId = session.catalog || session.catalogue;
            if (catalogueId && catalogueData[catalogueId]) {
                catalogueData[catalogueId].sessions.push(session);
            } else if (catalogueId === 'all' || validResults.length === 1) {
                // Se il catalogo è 'all' o c'è solo un catalogo, aggiungi a tutti
                Object.keys(catalogueData).forEach(key => {
                    catalogueData[key].sessions.push(session);
                });
            }
        });

        // 3️⃣ Preparazione dati per il report
        // Prepara i dati per l'Excel in base ai servizi selezionati
        const reportData = {};

        // Lista di servizi da includere nel report
        const servicesToInclude = [];

        // Se 'all' è incluso, aggiungiamo ALL SERVICES e anche i singoli servizi
        if (selectedOptions.includes('all')) {
            servicesToInclude.push("ALL SERVICES");
            // Aggiungi anche i singoli servizi
            servicesToInclude.push("WEB VTO", "3D VIEWER", "APP");
        } else {
            // Altrimenti, includi solo i servizi selezionati
            selectedOptions.forEach(option => {
                if (option === "WEB VTO" || option === "3D VIEWER" || option === "APP") {
                    servicesToInclude.push(option);
                }
            });
        }


        // Aggiungi foglio 'ALL SERVICES' se richiesto
        if (servicesToInclude.includes("ALL SERVICES")) {
            const allServicesResults = validResults.map(catalogue => {
                const catalogueSessions = catalogueData[catalogue.id]?.sessions || [];
                // Calcola le metriche totali per questo catalogo
                return calculateCatalogueMetrics(catalogue, catalogueSessions);
            });

            // Aggiungi totale generale
            const allSessions = Object.values(catalogueData).flatMap(d => d.sessions);

            allServicesResults.push({
                nomeBrand: "TOTALS",
                ...calculateTotalMetrics(allSessions)
            });

            reportData["ALL SERVICES"] = allServicesResults;
        }

        // Aggiungi fogli per i singoli servizi
        ["WEB VTO", "3D VIEWER", "APP"].forEach(category => {
            if (servicesToInclude.includes(category)) {
                const categoryResults = validResults.map(catalogue => {
                    const catalogueSessions = (catalogueData[catalogue.id]?.sessions || [])
                        .filter(session => {
                            // Verifica che il servizio corrisponda alla categoria
                            // Consideriamo anche le maiuscole/minuscole e i casi speciali
                            const sessionService = (session.service || "").toUpperCase();
                            const normalizedCategory = category === "3D VIEWER" ? "3D VIEWER" : category;

                            if (category === "3D VIEWER" && (sessionService === "3D" || sessionService === "3D VIEWER")) {
                                return true;
                            }

                            return sessionService === normalizedCategory;
                        });

                    return calculateCatalogueMetrics(catalogue, catalogueSessions);
                });

                // Aggiungi totale per categoria
                const allCategorySessions = Object.values(catalogueData)
                    .flatMap(d => d.sessions.filter(s => {
                        const sessionService = (s.service || "").toUpperCase();
                        const normalizedCategory = category === "3D VIEWER" ? "3D VIEWER" : category;

                        if (category === "3D VIEWER" && (sessionService === "3D" || sessionService === "3D VIEWER")) {
                            return true;
                        }

                        return sessionService === normalizedCategory;
                    }));

                categoryResults.push({
                    nomeBrand: "TOTALS",
                    ...calculateTotalMetrics(allCategorySessions)
                });

                reportData[category] = categoryResults;
            }
        });

        // Genera il file Excel
        generateExcelFile(reportData, selectedOptions);

        return true;
    } catch (error) {
        console.error("Error generating report:", error);
        throw error;
    }
}

// Funzione ausiliaria per calcolare le metriche totali
function calculateTotalMetrics(sessions) {
    // Filtra le sessioni valide (rimuove quelle senza dati)
    const validSessions = sessions.filter(session =>
        session && (
            (session.totalUniqueUsers || session.uniqueUsers || 0) > 0 ||
            (session.totalSessions || session.sessions || 0) > 0
        )
    );

    // Debug delle sessioni
    if (validSessions.length === 0) {
        return {
            totalUsers: 0,
            totalNewUsers: 0,
            totalSession: 0,
            avgSessionTimeTotal: "0:00",
            avgEngTimeTotal: "0:00",
            avgSessionPerUserTotal: 0
        };
    }

    // Calcola i totali dalle sessioni valide
    const totalUsers = validSessions.reduce((sum, session) =>
        sum + parseInt(session.totalUniqueUsers || session.uniqueUsers || 0), 0);

    const totalNewUsers = validSessions.reduce((sum, session) =>
        sum + parseInt(session.newUsersCount || session.newUserCount || 0), 0);

    const totalSession = validSessions.reduce((sum, session) =>
        sum + parseInt(session.totalSessions || session.sessions || 0), 0);

    // Utilizziamo lo stesso approccio di AnalyticsOverviewCard.js
    // Accumuliamo i secondi ponderati per ciascuna sessione/utente
    let totalAvgSessionTime = 0;
    let totalAvgEngTime = 0;

    validSessions.forEach((session) => {
        const sessionCount = parseInt(session.totalSessions || session.sessions || 0);
        const userCount = parseInt(session.totalUniqueUsers || session.uniqueUsers || 0);

        // Estrai i secondi di tempo medio sessione (come in AnalyticsOverviewCard)
        let avgSessionSecs = 0;
        if (session.avg_session_time) {
            avgSessionSecs = getTimeInSecs(session.avg_session_time);
        } else if (session.avgSessionTime) {
            avgSessionSecs = getTimeInSecs(session.avgSessionTime);
        }

        // Estrai i secondi di tempo medio engagement
        let avgEngagementSecs = 0;
        if (session.avg_engagement_time) {
            avgEngagementSecs = getTimeInSecs(session.avg_engagement_time);
        } else if (session.avgEngagementTime) {
            avgEngagementSecs = getTimeInSecs(session.avgEngagementTime);
        }

        // Accumula i tempi pesati come in AnalyticsOverviewCard
        totalAvgSessionTime += avgSessionSecs * sessionCount;
        totalAvgEngTime += avgEngagementSecs * userCount;
    });

    // Calcolo delle medie finali
    // Usa convertiSecondiAMinuti per formattare il risultato finale nel formato mm:ss
    const avgTimeTotalSecs = totalSession > 0 ? totalAvgSessionTime / totalSession : 0;
    const avgEngTimeTotalSecs = totalUsers > 0 ? totalAvgEngTime / totalUsers : 0;

    // Formatta usando la funzione di formattazione standard
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const avgSessionTimeTotal = formatTime(avgTimeTotalSecs);
    const avgEngTimeTotal = formatTime(avgEngTimeTotalSecs);

    // Calcola sessioni medie per utente
    const avgSessionPerUserTotal = totalUsers > 0
        ? parseFloat((totalSession / totalUsers).toFixed(2))
        : 0;

    return {
        totalUsers,
        totalNewUsers,
        totalSession,
        avgSessionTimeTotal,
        avgEngTimeTotal,
        avgSessionPerUserTotal
    };
}

// Funzione ausiliaria per calcolare le metriche di un singolo catalogo
function calculateCatalogueMetrics(catalogue, sessions) {
    // Filtra le sessioni valide per questo catalogo
    const validSessions = sessions.filter(session =>
        session && (
            (session.totalUniqueUsers || session.uniqueUsers || 0) > 0 ||
            (session.totalSessions || session.sessions || 0) > 0
        )
    );

    // Calcola i totali dalle sessioni valide
    const totalUsers = validSessions.reduce((sum, session) =>
        sum + parseInt(session.totalUniqueUsers || session.uniqueUsers || 0), 0);

    const totalNewUsers = validSessions.reduce((sum, session) =>
        sum + parseInt(session.newUsersCount || session.newUserCount || 0), 0);

    const totalSession = validSessions.reduce((sum, session) =>
        sum + parseInt(session.totalSessions || session.sessions || 0), 0);

    // Utilizziamo lo stesso approccio di AnalyticsOverviewCard.js
    // Accumuliamo i secondi ponderati per ciascuna sessione/utente
    let totalAvgSessionTime = 0;
    let totalAvgEngTime = 0;

    validSessions.forEach((session) => {
        const sessionCount = parseInt(session.totalSessions || session.sessions || 0);
        const userCount = parseInt(session.totalUniqueUsers || session.uniqueUsers || 0);

        // Estrai i secondi di tempo medio sessione (come in AnalyticsOverviewCard)
        let avgSessionSecs = 0;
        if (session.avg_session_time) {
            avgSessionSecs = getTimeInSecs(session.avg_session_time);
        } else if (session.avgSessionTime) {
            avgSessionSecs = getTimeInSecs(session.avgSessionTime);
        }

        // Estrai i secondi di tempo medio engagement
        let avgEngagementSecs = 0;
        if (session.avg_engagement_time) {
            avgEngagementSecs = getTimeInSecs(session.avg_engagement_time);
        } else if (session.avgEngagementTime) {
            avgEngagementSecs = getTimeInSecs(session.avgEngagementTime);
        }

        // Accumula i tempi pesati come in AnalyticsOverviewCard
        totalAvgSessionTime += avgSessionSecs * sessionCount;
        totalAvgEngTime += avgEngagementSecs * userCount;
    });

    // Calcolo delle medie finali
    const avgTimeTotalSecs = totalSession > 0 ? totalAvgSessionTime / totalSession : 0;
    const avgEngTimeTotalSecs = totalUsers > 0 ? totalAvgEngTime / totalUsers : 0;

    // Formatta usando la funzione di formattazione standard
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const avgSessionTimeTotal = formatTime(avgTimeTotalSecs);
    const avgEngTimeTotal = formatTime(avgEngTimeTotalSecs);

    // Calcola sessioni medie per utente
    const avgSessionPerUserTotal = totalUsers > 0
        ? parseFloat((totalSession / totalUsers).toFixed(2))
        : 0;

    const metrics = {
        totalUsers,
        totalNewUsers,
        totalSession,
        avgSessionTimeTotal,
        avgEngTimeTotal,
        avgSessionPerUserTotal
    };

    return {
        nomeBrand: catalogue.nameCatalog || catalogue.nome_brand || catalogue.id,
        ...metrics
    };
}

export const fetchSubcollectionData = async (docId, subcollectionName) => {
    try {
        // Ottieni il riferimento alla sottocollezione
        const subcollectionRef = collection(db, "AggregateSessions", docId, subcollectionName);

        // Recupera i documenti dalla sottocollezione
        const subcollectionSnapshot = await getDocs(subcollectionRef);

        // Mappa i documenti in un array di oggetti
        const subcollectionData = subcollectionSnapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
        }));

        return subcollectionData;
    } catch (error) {
        return [];
    }
};